// src/pages/orders/components/OrderItemsSection.jsx
import React, { useState, useRef } from 'react';
import {
  Search,
  Plus,
  X,
  Package,
  Hash,
  DollarSign,
  Minus,
  AlertCircle,
  Scan,
  Percent
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import useProductSearch from '../../../hooks/useProductSearch';
import { calculateLineTotal, getCrossSellMaxQty } from '../../../utils/orderCalculations';
import DiscountPercentagePopup from '../../../components/ui/DiscountPercentagePopup';
import BundleBadge from '../../../components/ui/BundleBadge/BundleBadge';
import BundleItemCard from '../../../components/orders/BundleItemCard/BundleItemCard';
import { groupOrderItems, getBundleCrossSellMax, getBundleCrossSellCount } from '../../../utils/orderItemGrouping';
import Dropdown from '../../../components/ui/Dropdown/Dropdown';
import styles from './OrderItemsSection.module.css';

const OrderItemsSection = ({
  shopId,
  orderItems,
  errors,
  canChangeUnitPrice,
  canGiveDiscount,
  crossSellEnabled = false,
  currentUserId,
  createdByUserId,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onUpdateBundleQuantity,
  onRemoveBundle,
  onSetBundleCrossSell,
  allowDiscountOverLine = false
}) => {
  const { t } = useLocalization();
  const [isSearching, setIsSearching] = useState(false);
  const [localInputValues, setLocalInputValues] = useState({});
  // { itemId, anchorEl } — anchorEl is the % button DOM node used for portal positioning
  const [discountPopupState, setDiscountPopupState] = useState(null);
  const searchInputRef = useRef(null);

  const {
    searchTerm,
    searchResults,
    isSearching: isLoadingResults,
    selectedIndex,
    showResults,
    hasSearched,
    resultsRef,
    handleSearchChange,
    handleKeyDown: productSearchKeyDown,
    selectProduct,
    clearSearch,
    hideResults,
    showResultsDropdown
  } = useProductSearch(shopId);

  const handleProductSelect = (product) => {
    const selectedProduct = selectProduct(product);
    onAddItem(selectedProduct);
    clearSearch();
    setIsSearching(false);
    
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      if (searchResults.length > 0) {
        const selectedProduct = selectedIndex >= 0 
          ? searchResults[selectedIndex] 
          : searchResults[0];
        
        handleProductSelect(selectedProduct);
        return;
      }
    }
    
    const selectedProduct = productSearchKeyDown(e);
    if (selectedProduct) {
      handleProductSelect(selectedProduct);
    }
  };

  const formatNumberForDisplay = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return num % 1 === 0 ? num.toString() : num.toString();
  };

  const getPriceDisplayValue = (item, field) => {
    const key = `${item.id}-${field}`;
    if (key in localInputValues) return localInputValues[key];
    const raw = field === 'unitPrice' ? item.unitPrice : (item.lineDiscount || 0);
    return formatNumberForDisplay(raw);
  };

  const handlePriceChange = (e, field, itemId) => {
    const raw = e.target.value;
    const key = `${itemId}-${field}`;
    if (raw === '') {
      setLocalInputValues(prev => ({ ...prev, [key]: raw }));
      onUpdateItem(itemId, field, 0);
      return;
    }
    // Allow only valid numeric strings with up to 2 decimal places
    if (!/^\d*\.?\d{0,2}$/.test(raw)) return;
    const numValue = parseFloat(raw);
    if (field === 'lineDiscount' && !allowDiscountOverLine && !isNaN(numValue)) {
      const item = orderItems.find(i => i.id === itemId);
      const lineGross = (parseFloat(item?.quantity) || 0) * (parseFloat(item?.unitPrice) || 0);
      if (numValue > lineGross) return;
    }
    setLocalInputValues(prev => ({ ...prev, [key]: raw }));
    if (!isNaN(numValue)) {
      onUpdateItem(itemId, field, numValue);
    }
  };

  const handlePriceBlur = (itemId, field) => {
    const key = `${itemId}-${field}`;
    setLocalInputValues(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const renderSearchResults = () => {
    if (!showResults || !shopId) return null;

    return (
      <div className={styles.searchResults} ref={resultsRef}>
        {isLoadingResults ? (
          <div className={styles.searchLoading}>
            <div className={styles.loadingSpinner}></div>
            <span>{t('common.loading')}</span>
          </div>
        ) : searchResults.length > 0 ? (
          <div className={styles.resultsList}>
            {searchResults.map((product, index) => (
              <div
                key={`${product.productId}-${product.variationId || 'simple'}`}
                className={`${styles.resultItem} ${
                  index === selectedIndex ? styles.selected : ''
                } ${product.isOutOfStock ? styles.outOfStock : ''}`}
                onClick={() => handleProductSelect(product)}
              >
                <div className={styles.productInfo}>
                  <div className={styles.productName}>
                    {product.formattedName}
                    {product.type === 'bundle' && <> <BundleBadge size={10} /></>}
                  </div>
                  <div className={styles.productDetails}>
                    <span className={styles.productSku}>
                      <Hash size={12} />
                      {product.sku || 'No SKU'}
                    </span>
                    {!product.stockHidden && (
                      <span className={styles.productStock}>
                        <Package size={12} />
                        {product.formattedStock}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.productPrice}>
                  {product.formattedPrice}
                </div>
              </div>
            ))}
          </div>
        ) : hasSearched ? (
          <div className={styles.noResults}>
            <AlertCircle size={16} />
            <span>{t('ecomOrders.noProductsFound')}</span>
          </div>
        ) : null}
      </div>
    );
  };

  const renderOrderItem = (item, index) => {
    const lineCalculation = calculateLineTotal(
      item.quantity,
      item.unitPrice,
      item.lineDiscount || 0
    );

    const isCrossSoldByOther = item.crossSellById != null && item.crossSellById !== currentUserId;
    const crossSellMaxQty = getCrossSellMaxQty(item, { currentUserId, createdByUserId });
    const crossSellDisabledNew = !item.isCrossSell && crossSellMaxQty <= 0;

    return (
      <div key={item.id} className={styles.orderItem}>
        <div className={styles.itemHeader}>
          <div className={styles.itemInfo}>
            <h4 className={styles.itemName}>{item.displayName}</h4>
            {item.sku && (
              <span className={styles.itemSku}>
                <Hash size={12} />
                {item.sku}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => onRemoveItem(item.id)}
            className={styles.removeItemButton}
            disabled={isCrossSoldByOther}
            title={isCrossSoldByOther
              ? (t('ecomOrders.cannotRemoveOthersCrossSell') || 'Only the cross-sell owner can remove this item')
              : t('ecomOrders.removeItem')}
          >
            <X size={16} />
          </button>
        </div>

        <div className={`${styles.itemFields} ${crossSellEnabled ? styles.itemFieldsWithCrossSell : ''}`}>
          <div className={styles.fieldGroup}>
            <label>{t('ecomOrders.quantity')} *</label>
            <div className={styles.quantityWrapper}>
              <button
                type="button"
                onClick={() => onUpdateItem(item.id, 'quantity', Math.max(1, item.quantity - 1), { currentUserId, createdByUserId })}
                className={styles.quantityButton}
                disabled={item.quantity <= 1 || isCrossSoldByOther}
              >
                <Minus size={14} />
              </button>
              <input
                type="number"
                value={formatNumberForDisplay(item.quantity)}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    onUpdateItem(item.id, 'quantity', 1, { currentUserId, createdByUserId });
                  } else {
                    const intValue = parseInt(value);
                    if (!isNaN(intValue) && intValue >= 1) {
                      onUpdateItem(item.id, 'quantity', intValue, { currentUserId, createdByUserId });
                    }
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === 'e') {
                    e.preventDefault();
                  }
                }}
                min="1"
                step="1"
                disabled={isCrossSoldByOther}
                className={`${styles.quantityInput} ${
                  errors.orderItemDetails?.[index]?.quantity ? styles.error : ''
                }`}
              />
              <button
                type="button"
                onClick={() => onUpdateItem(item.id, 'quantity', parseFloat(item.quantity) + 1, { currentUserId, createdByUserId })}
                className={styles.quantityButton}
                disabled={isCrossSoldByOther}
              >
                <Plus size={14} />
              </button>
            </div>
            {errors.orderItemDetails?.[index]?.quantity && (
              <span className={styles.errorText}>
                {errors.orderItemDetails[index].quantity}
              </span>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <label>
              {t('ecomOrders.unitPrice')} *
              {!canChangeUnitPrice && (
                <span className={styles.lockedField}>🔒</span>
              )}
            </label>
            <div className={styles.inputWrapper}>
              <DollarSign className={styles.inputIcon} size={16} />
              <input
                type="text"
                value={getPriceDisplayValue(item, 'unitPrice')}
                onChange={(e) => handlePriceChange(e, 'unitPrice', item.id)}
                onBlur={() => handlePriceBlur(item.id, 'unitPrice')}
                disabled={!canChangeUnitPrice || isCrossSoldByOther}
                className={`${styles.priceInput} ${
                  errors.orderItemDetails?.[index]?.unitPrice ? styles.error : ''
                }`}
              />
            </div>
            {errors.orderItemDetails?.[index]?.unitPrice && (
              <span className={styles.errorText}>
                {errors.orderItemDetails[index].unitPrice}
              </span>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <label>
              {t('ecomOrders.lineDiscount')}
              {(!canGiveDiscount || isCrossSoldByOther) && (
                <span className={styles.lockedField}>🔒</span>
              )}
            </label>
            <div className={styles.discountFieldRow}>
              <div className={styles.inputWrapper}>
                <DollarSign className={styles.inputIcon} size={16} />
                <input
                  type="text"
                  value={getPriceDisplayValue(item, 'lineDiscount')}
                  onChange={(e) => handlePriceChange(e, 'lineDiscount', item.id)}
                  onBlur={() => handlePriceBlur(item.id, 'lineDiscount')}
                  disabled={!canGiveDiscount || isCrossSoldByOther}
                  placeholder="0"
                  className={`${styles.priceInput} ${
                    errors.orderItemDetails?.[index]?.lineDiscount ? styles.error : ''
                  }`}
                />
              </div>
              {canGiveDiscount && !isCrossSoldByOther && (
                <button
                  type="button"
                  className={`${styles.discountPctBtn} ${discountPopupState?.itemId === item.id ? styles.discountPctBtnActive : ''}`}
                  onClick={(e) => setDiscountPopupState(
                    discountPopupState?.itemId === item.id
                      ? null
                      : { itemId: item.id, anchorEl: e.currentTarget }
                  )}
                  title="Set discount as percentage"
                >
                  <Percent size={12} />
                </button>
              )}
              {discountPopupState?.itemId === item.id && (
                <DiscountPercentagePopup
                  anchorEl={discountPopupState.anchorEl}
                  unitPrice={item.unitPrice}
                  quantity={item.quantity}
                  onApply={(amount) => {
                    // Clear the local input so the canonical form value displays immediately
                    setLocalInputValues(prev => {
                      const next = { ...prev };
                      delete next[`${item.id}-lineDiscount`];
                      return next;
                    });
                    onUpdateItem(item.id, 'lineDiscount', allowDiscountOverLine
                      ? amount
                      : Math.min(amount, (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0)));
                  }}
                  onClose={() => setDiscountPopupState(null)}
                />
              )}
            </div>
            {errors.orderItemDetails?.[index]?.lineDiscount && (
              <span className={styles.errorText}>
                {errors.orderItemDetails[index].lineDiscount}
              </span>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <label>{t('ecomOrders.lineTotal')}</label>
            <div className={styles.lineTotalDisplay}>
              Rs. {lineCalculation.lineTotal.toFixed(2)}
            </div>
          </div>

          {crossSellEnabled && (
            <div className={styles.fieldGroup}>
              <label title={crossSellDisabledNew ? (t('ecomOrders.crossSellNoEligibleQty') || 'No new/increased quantity to cross-sell') : undefined}>
                {t('ecomOrders.crossSellItem') || 'Cross Sell'}
              </label>
              <div className={styles.crossSellControl}>
                <div className={styles.crossSellCheckboxBox}>
                  <input
                    type="checkbox"
                    className={styles.crossSellCheckbox}
                    checked={!!item.isCrossSell}
                    disabled={isCrossSoldByOther || crossSellDisabledNew}
                    onChange={(e) => onUpdateItem(item.id, 'isCrossSell', e.target.checked, { maxCrossSellQty: crossSellMaxQty })}
                  />
                </div>
                <Dropdown
                  className={styles.crossSellQtyDropdown}
                  panelClassName={styles.crossSellQtyPanel}
                  value={item.isCrossSell ? (Number(item.crossSellQuantity) || 1) : 0}
                  onChange={(v) => v != null && onUpdateItem(item.id, 'crossSellQuantity', Number(v), { maxCrossSellQty: crossSellMaxQty })}
                  options={item.isCrossSell
                    ? Array.from({ length: Math.max(item.crossSellQuantity || 0, crossSellMaxQty) }, (_, i) => ({ value: i + 1, label: String(i + 1) }))
                    : [{ value: 0, label: '0' }]}
                  disabled={!item.isCrossSell || isCrossSoldByOther}
                  allowClear={false}
                  size="compact"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderBundle = (group) => {
    const ctx = { currentUserId, createdByUserId };
    const first = group.items[0];
    return (
      <BundleItemCard
        key={group.groupKey}
        group={group}
        onQuantityChange={(qty) => onUpdateBundleQuantity(group.groupKey, qty)}
        onRemove={() => onRemoveBundle(group.groupKey)}
        crossSell={crossSellEnabled ? {
          checked: !!first?.isCrossSell,
          count: getBundleCrossSellCount(group.items),
          maxQty: getBundleCrossSellMax(group.items, ctx),
          lockedByOther: first?.crossSellById != null && first.crossSellById !== currentUserId,
          onChange: (enabled, n) => onSetBundleCrossSell(group.groupKey, enabled, n),
        } : null}
      />
    );
  };

  if (!shopId) {
    return (
      <div className={styles.orderItemsContent}>
        <div className={styles.noShopMessage}>
          <AlertCircle size={24} />
          <p>{t('ecomOrders.selectShopRequired')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.orderItemsContent}>
      {/* Product Search */}
      <div className={styles.productSearch}>
        <div className={styles.searchSection}>
          <div className={styles.searchHeader}>
            <h3>{t('ecomOrders.searchProducts')}</h3>
            <div className={styles.barcodeBadge}>
              <Scan size={14} />
              <span>Barcode Supported</span>
            </div>
          </div>
          <div className={styles.searchWrapper}>
            <div className={styles.searchInputWrapper}>
              <Search className={styles.searchIcon} size={18} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onFocus={showResultsDropdown}
                onBlur={() => setTimeout(hideResults, 200)}
                placeholder={t('ecomOrders.searchProductsPlaceholder') || 'Search by name, SKU, or scan barcode...'}
                className={styles.searchInput}
                autoComplete="off"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className={styles.clearSearchButton}
                >
                  <X size={16} />
                </button>
              )}
            </div>
            {renderSearchResults()}
          </div>
        </div>
      </div>

      {/* Order Items List */}
      <div className={styles.orderItemsList}>
        {orderItems.length > 0 ? (
          <div className={styles.itemsContainer}>
            <div className={styles.itemsList}>
{groupOrderItems(orderItems).map((entry, index) => (entry.type === 'bundle'
                ? renderBundle(entry)
                : renderOrderItem(entry.item, index)))}
            </div>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <Package size={48} />
            <h3>{t('ecomOrders.atLeastOneItemRequired')}</h3>
            <p>Search and add products to create the order</p>
          </div>
        )}
        
        {errors.orderItems && (
          <div className={styles.sectionError}>
            <AlertCircle size={16} />
            {errors.orderItems}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderItemsSection;